import {
  GetApplicationsQueryResult,
  Person,
  SeagoingResult,
} from '@sdir/httpclient/lib/clients/aps/application';
import {
  ProcessingState,
  ServiceReport,
  ServiceReportExtended,
  ServiceReportResult,
  ServiceReportValidationReport,
  Vessel,
} from '@sdir/httpclient/lib/clients/aps/seagoing';
import { AxiosResponse } from 'axios';
import moment from 'moment';
import { RegisterSeagoingInput } from '@sdir/blueprint.aps/lib/components';
import { seagoingApi, seagoingServicesApi } from '../httpclient';
import { AddedSeagoing, AddedSeagoingDetails } from '../types/state';
import { SubmitItem, SubmitStatus } from '../types/submitSequence';
import {
  buildSubmitAttachmentUpdatesSequence,
  SubmitAttachmentUpdatesParams,
  SubmitAttachmentUpdatesTracker,
} from './attachmentApiHelpers';
import { createQualificationApprovalDetails } from './qualificationApiHelpers';
import { addToSequence } from './submitSequenceHelpers';
import { processPrevalidationReport } from './validationReportHelper';

export const createNewSeagoingPayload = (values, application) => {
  const { attachment, attachmentUid, seagoing, vessel } = values;
  const { person, uid: applicationUid } = application;
  const grossTonnage = `${vessel?.vesselGrossTonnage || ''}`.replaceAll('.', ',');
  const lengthOverall = `${vessel?.vesselLengthOverall || ''}`.replaceAll('.', ',');
  const propulsionPower = `${vessel?.vesselPropulsionPower || ''}`.replaceAll('.', ',');
  return {
    personNumber: person?.personalIdentificationNumber || '',
    signOnDate: seagoing.signOnDate ? moment(seagoing.signOnDate)?.format('YYYY-MM-DD') : '',
    signOffDate: seagoing.signOffDate ? moment(seagoing.signOffDate)?.format('YYYY-MM-DD') : '',
    totalServiceHours: seagoing?.totalServiceHours,
    serviceHoursPerDay: seagoing?.serviceHoursPerDay,
    serviceCode: seagoing?.occupationCode,
    callSign: vessel?.callSign,
    vesselName: vessel?.vesselName,
    dataAccumulatedAt: new Date().toISOString(),
    vesselGrossTonnage: grossTonnage,
    vesselLengthOverall: lengthOverall,
    vesselLengthOverallUnit: vessel.vesselLengthOverallUnit || 'Meter',
    vesselPropulsionPower: propulsionPower,
    applicationUid: applicationUid || '',
    attachmentUid: attachmentUid || '',
    attachmentFile: attachment,
    simulatorResultItemUri: '',
    imoNumber: vessel?.imoNumber || '',
    vesselId: vessel?.vesselId || '',
    vesselRadioCoverage: vessel?.vesselRadioCoverage || '',
    vesselType: vessel?.vesselType || '',
    vesselTypeCode: vessel.vesselTypeCode || '',
    vesselTradeArea: vessel?.vesselTradeArea || '',
    vesselTradeAreaUri: vessel.vesselTradeAreaUri || '',
    vesselPropulsionType: vessel?.vesselPropulsionType || '',
    vesselPropulsionTypeDescription: vessel.vesselPropulsionTypeDescription || '',
  };
};

export const createNewUpdateSeagoingPayload = (values, application, selectedSeagoing) => {
  const { attachmentUid, attachment, seagoing, vessel } = values;
  const { person, uid: applicationUid } = application;
  const grossTonnage = `${vessel?.vesselGrossTonnage || ''}`.replaceAll('.', ',');
  const lengthOverall = `${vessel?.vesselLengthOverall || ''}`.replaceAll('.', ',');
  const propulsionPower = `${vessel?.vesselPropulsionPower || ''}`.replaceAll('.', ',');
  return {
    uid: selectedSeagoing?.uid,
    personNumber: person?.personalIdentificationNumber || '',
    signOnDate: seagoing.signOnDate ? moment(seagoing.signOnDate)?.format('YYYY-MM-DD') : '',
    signOffDate: seagoing.signOffDate ? moment(seagoing.signOffDate)?.format('YYYY-MM-DD') : '',
    totalServiceHours: seagoing?.totalServiceHours,
    serviceHoursPerDay: seagoing?.serviceHoursPerDay,
    serviceCode: seagoing?.occupationCode,
    callSign: vessel?.callSign,
    vesselName: vessel?.vesselName,
    dataAccumulatedAt: new Date().toISOString(),
    vesselGrossTonnage: grossTonnage,
    vesselLengthOverall: lengthOverall,
    vesselLengthOverallUnit: vessel.vesselLengthOverallUnit || 'Meter',
    vesselPropulsionPower: propulsionPower,
    seagoingUid: selectedSeagoing?.uid,
    applicationUid: applicationUid || '',
    attachmentUid: attachment ? '' : attachmentUid,
    attachmentFile: attachment,
    simulatorResultItemUri: '',
    imoNumber: vessel?.imoNumber || '',
    vesselId: vessel?.vesselId || '',
    vesselRadioCoverage: vessel?.vesselRadioCoverage || '',
    vesselType: vessel?.vesselType,
    vesselTypeCode: vessel.vesselTypeCode || '',
    vesselTradeArea: vessel?.vesselTradeArea,
    vesselTradeAreaUri: vessel.vesselTradeAreaUri || '',
    vesselPropulsionType: vessel?.vesselPropulsionType,
    vesselPropulsionTypeDescription: vessel.vesselPropulsionTypeDescription || '',
  };
};

export const submitNewSeagoingCreate = (
  values: RegisterSeagoingInput,
  application: GetApplicationsQueryResult
): Promise<AxiosResponse<SeagoingResult>> => {
  const createBody = createNewSeagoingPayload(values, application);
  return seagoingApi.v1SeagoingCreateseagoingforapplicationVesselRadioCoveragePost(
    createBody.personNumber,
    createBody.signOnDate,
    createBody.signOffDate,
    createBody.totalServiceHours,
    createBody.serviceCode,
    createBody.callSign,
    createBody.vesselName,
    createBody.dataAccumulatedAt,
    createBody.vesselType,
    createBody.vesselTradeArea,
    createBody.vesselLengthOverall,
    createBody.vesselLengthOverallUnit,
    createBody.vesselPropulsionType,
    createBody.applicationUid,
    createBody.attachmentUid,
    createBody.attachmentFile,
    createBody.simulatorResultItemUri,
    createBody.imoNumber,
    createBody.vesselId,
    createBody.vesselGrossTonnage,
    createBody.vesselPropulsionPower,
    createBody.vesselRadioCoverage
  );
};

export const submitNewSeagoingUpdate = (
  values: RegisterSeagoingInput,
  application: GetApplicationsQueryResult,
  seagoing: any
): Promise<AxiosResponse<SeagoingResult>> => {
  const updatePayload = createNewUpdateSeagoingPayload(values, application, seagoing);
  return seagoingApi.v1SeagoingUpdateseagoingforapplicationVesselRadioCoveragePut(
    updatePayload.uid,
    updatePayload.personNumber,
    updatePayload.signOnDate,
    updatePayload.signOffDate,
    updatePayload.totalServiceHours,
    updatePayload.serviceCode,
    updatePayload.callSign,
    updatePayload.vesselName,
    updatePayload.dataAccumulatedAt,
    updatePayload.vesselType,
    updatePayload.vesselTradeArea,
    updatePayload.vesselLengthOverall,
    updatePayload.vesselLengthOverallUnit,
    updatePayload.vesselPropulsionType,
    updatePayload.seagoingUid,
    updatePayload.applicationUid,
    updatePayload.attachmentUid,
    updatePayload.attachmentFile,
    updatePayload.simulatorResultItemUri,
    updatePayload.imoNumber,
    updatePayload.vesselId,
    updatePayload.vesselGrossTonnage,
    updatePayload.vesselPropulsionPower,
    updatePayload.vesselRadioCoverage
  );
};

export const formatBackendValidationErrors = (apiErrors) => {
  const errors = {};
  let otherError;
  for (let i = 0; i < apiErrors.length; i += 1) {
    const item = apiErrors[i];
    // This code will be improved after next version of blueprint.aps
    let key = item.property.split('.')?.[1] || '';
    key = `${key.charAt(0).toLowerCase()}${key.substring(1, key.length)}`;
    if (key) {
      errors[key] = item.message || undefined;
    } else if (item.property && item.property.indexOf('.') === -1) {
      // Has error without a related field
      otherError = item.message;
    }
  }
  return { fieldErrors: errors, otherError };
};

const createSeagoingPayloadItem = (
  vessel: Vessel,
  seagoingDetail: AddedSeagoingDetails,
  applicationUid: string,
  person: Person,
  attachmentGroup: string
): ServiceReport => {
  return {
    personLegalEntityId: person.legalEntityId,
    personalIdentityNumber: person.personalIdentificationNumber,
    // TODO: handle new/manual organization differently?
    organizationNumber: '',
    vessel,
    activity: {
      occupationCode: seagoingDetail.occupationCode,
      // API expects integer for hours
      totalHours: Math.round(seagoingDetail.totalHours),
      startDate: seagoingDetail.startDate.format('YYYY-MM-DD'),
      endDate: seagoingDetail.endDate.format('YYYY-MM-DD'),
      totalDays: (Math.round(seagoingDetail.totalHours) || 0) / 8,
    },
    qualificationApprovalDetails: createQualificationApprovalDetails(
      applicationUid,
      // to be removed in backend, use group as fallback for now
      attachmentGroup
    ),
    qualificationApprovalState: {
      processingState: ProcessingState.Unprocessed,
    },
    applicationGroupUid: attachmentGroup,
    applicationReference: applicationUid,
  };
};

export const createSeagoingPayload = (
  addedSeagoingGroups: AddedSeagoing[],
  applicationUid: string,
  person: Person,
  attachmentGroup: string
): ServiceReport[] => {
  return addedSeagoingGroups.flatMap(
    (addedSeagoing) =>
      addedSeagoing.details?.map<ServiceReport>((d) =>
        createSeagoingPayloadItem(addedSeagoing.vessel, d, applicationUid, person, attachmentGroup)
      ) || []
  );
};

export const createUpdatedSeagoingPayload = (
  seagoingList: AddedSeagoing[],
  applicationUid: string,
  person: Person,
  attachmentGroup: string
): ServiceReportResult[] => {
  return seagoingList.flatMap(
    (seagoing) =>
      seagoing.details.map<ServiceReportResult>((d) => ({
        ...createSeagoingPayloadItem(seagoing.vessel, d, applicationUid, person, attachmentGroup),
        uid: d.existingReport?.uid,
      })) || []
  );
};

export const submitAddedSeagoingData = (
  addedSeagoing: AddedSeagoing[],
  applicationUid: string,
  person: Person,
  attachmentGroup: string
): Promise<AxiosResponse<ServiceReportResult[]>> => {
  const payload = createSeagoingPayload(addedSeagoing, applicationUid, person, attachmentGroup);
  return seagoingServicesApi.v1SeagoingservicesAddservicereportsServiceReportsPost(payload);
};

export const submitUpdatedSeagoingData = (
  seagoing: AddedSeagoing[],
  applicationUid: string,
  person: Person,
  attachmentGroup: string
): Promise<AxiosResponse<ServiceReportResult[]>> => {
  const payload = createUpdatedSeagoingPayload(seagoing, applicationUid, person, attachmentGroup);
  return seagoingServicesApi.v1SeagoingservicesUpdateservicereportsServiceReportsPut(payload);
};

export const deleteSeagoingData = (
  uidsToDelete: string[]
): Promise<AxiosResponse<ServiceReportResult[]>> => {
  return seagoingServicesApi.v1SeagoingservicesDeleteservicereportsUidsDelete(
    uidsToDelete.join(',')
  );
};

export const prevalidateSeagoingData = (
  addedSeagoing: AddedSeagoing[],
  applicationUid: string,
  person: Person,
  attachmentGroup: string
): Promise<AxiosResponse<ServiceReportValidationReport>> => {
  const payload = createSeagoingPayload(addedSeagoing, applicationUid, person, attachmentGroup);
  return seagoingServicesApi.v1SeagoingservicesPrevalidateServiceReportsPost(payload);
};

export type SubmitSeagoingTracker = SubmitAttachmentUpdatesTracker & {
  addedSeagoing?: SubmitStatus;
  updatedSeagoing?: SubmitStatus;
  deletedSeagoing?: SubmitStatus;
};

export const buildSubmitSeagoingSequence = ({
  seagoingGroups,
  existing,
  ...fileUpdateParams
}: SubmitAttachmentUpdatesParams & {
  seagoingGroups: AddedSeagoing[];
  existing: ServiceReportExtended[];
}): SubmitItem<SubmitSeagoingTracker>[] => {
  const { application, attachmentGroup } = fileUpdateParams;

  const sequence: SubmitItem<SubmitSeagoingTracker>[] = [
    // Validate seagoing data
    {
      labelId: 'submitsteps.prevalidateseagoing',
      apiCall: () =>
        prevalidateSeagoingData(
          seagoingGroups,
          application.uid || '',
          application.person || {},
          attachmentGroup || ''
        ),
      processSuccessResult: processPrevalidationReport,
    },
    // Upload attachments
    ...buildSubmitAttachmentUpdatesSequence(fileUpdateParams),
  ];

  const deletedSeagoing = existing.filter(
    (e) => !seagoingGroups.find((s) => s.details.find((d) => d.existingReport?.uid === e.uid))
  );
  if (deletedSeagoing.length) {
    addToSequence(sequence, {
      labelId: 'submitsteps.deletedseagoing',
      apiCall: () => deleteSeagoingData(deletedSeagoing.map((x) => x.uid || '')),
      statusSelector: (ctx) => ctx.deletedSeagoing,
      statusSetter: (ctx, status) => ({ ...ctx, deletedSeagoing: status }),
    });
  }

  const updatedSeagoing = seagoingGroups
    .map((x) => ({ ...x, details: x.details.filter((d) => !!d.existingReport) }))
    .filter((x) => x.details.length);
  if (updatedSeagoing.length) {
    addToSequence(sequence, {
      labelId: 'submitsteps.updatedseagoing',
      apiCall: () => {
        return submitUpdatedSeagoingData(
          updatedSeagoing,
          application.uid || '',
          application.person || {},
          attachmentGroup || ''
        );
      },
      statusSelector: (ctx) => ctx.updatedSeagoing,
      statusSetter: (ctx, status) => ({ ...ctx, updatedSeagoing: status }),
    });
  }

  const addedSeagoing = seagoingGroups
    .map((x) => ({ ...x, details: x.details.filter((d) => !d.existingReport) }))
    .filter((x) => x.details.length);
  if (addedSeagoing.length) {
    addToSequence(sequence, {
      labelId: 'submitsteps.newseagoing',
      apiCall: () => {
        return submitAddedSeagoingData(
          addedSeagoing,
          application.uid || '',
          application.person || {},
          attachmentGroup || ''
        );
      },
      statusSelector: (ctx) => ctx.addedSeagoing,
      statusSetter: (ctx, status) => ({ ...ctx, addedSeagoing: status }),
    });
  }

  return sequence;
};

type ServiceReportGroup = {
  reports: ServiceReportExtended[];
  vessel: Vessel;
  organizationNumber: string;
};

const matchesGroup = (group: ServiceReportGroup, item: ServiceReportExtended) =>
  group.organizationNumber === item.organizationNumber &&
  group.vessel.callSign === item.vessel.callSign &&
  group.vessel.imoNumber === item.vessel.imoNumber;

const addToGroup = (
  currentGroups: ServiceReportGroup[],
  newItem: ServiceReportExtended
): ServiceReportGroup[] => {
  const matchingGroupIndex = currentGroups.findIndex((g) => matchesGroup(g, newItem));
  if (matchingGroupIndex >= 0) {
    const matchingGroup = currentGroups[matchingGroupIndex];
    return [
      ...currentGroups.slice(0, matchingGroupIndex),
      {
        ...matchingGroup,
        reports: [...matchingGroup.reports, newItem],
      },
      ...currentGroups.slice(matchingGroupIndex + 1),
    ];
  }
  return [
    ...currentGroups,
    {
      organizationNumber: newItem.organizationNumber || '',
      vessel: newItem.vessel,
      reports: [newItem],
    },
  ];
};

export const restoreAddedSeagoingState = async (
  existing: ServiceReportExtended[]
): Promise<AddedSeagoing[]> => {
  if (!existing.length) return [];

  const groupedExistingReports = existing.reduce<ServiceReportGroup[]>((res, item) => {
    return addToGroup(res, item);
  }, []);

  return groupedExistingReports.map(
    (g): AddedSeagoing => ({
      vessel: g.vessel,
      details: g.reports.map((x) => ({
        totalHours: x.activity.totalHours,
        startDate: moment(x.activity.startDate),
        endDate: moment(x.activity.endDate),
        occupationCode: x.activity.occupationCode,
        existingReport: x,
      })),
    })
  );
};
